/**
 * TeamControlMap — per-team "control map" card pair (PMA-COCK-162).
 *
 * For each team: crest + name, a pitch shaded in the team's color by cell
 * intensity, an attacking-direction arrow, and the thirds/lanes shares
 * computed by `teamControlMap.ts`. Mounted directly below the combined
 * Area Control grid in HeatMapSection (public share + cockpit areaControl
 * view only, never the per-player view).
 *
 * Orientation convention (see teamControlMap.ts header for the full
 * rationale): one absolute location frame for the whole match, home
 * attacks toward x=120 (arrow →), away attacks toward x=0 (arrow ←).
 *
 * Client wave 2026-09-19 (EPIC-61, brief 6/AC11 — PMA-COCK-212): the pitch
 * behind the control-intensity grid was light grey (`#e2e8f0`, slate-200)
 * instead of green, with the markings/mowing-stripes/spot-fills recoloured
 * dark slate (`rgba(15,23,42,...)`) for contrast — matched the client's
 * then-current reference image ("teal/red grid on light grey"). Both cards
 * show side by side from `sm:` (640px) instead of `md:` (768px), each
 * narrower (`max-w-[320px]`, was `max-w-sm`/384px), so the two teams stay
 * readable together rather than stacking with the second team below the
 * fold on a typical phone viewport — this is a public-facing page. That
 * breakpoint/width decision is UNCHANGED by the wave below.
 *
 * Client wave 2026-09-23 (EPIC-94, item 6/AC8 — dated reversal of
 * PMA-COCK-212's own colour choice, recorded by the wave's contract pass):
 * "Every pitch should use the colour of the large area-control map (the
 * darker green)." The pitch goes back to `#2d6a2e`/`bg-green-800` (the same
 * fill `AreaControlGrid.tsx`/`PassMapPitch.tsx` already use) with the
 * markings/mowing-stripes/spot-fills back to white
 * (`rgba(255,255,255,...)`), matching those two files' own established
 * tone — legible because the cell-intensity alpha range this file already
 * uses (`MIN_VISIBLE_ALPHA` 0.25 .. `MAX_ALPHA` 0.85, unchanged by this
 * pass) is the same non-muddy range `AreaControlGrid.tsx` was fixed to use
 * against this exact green (see that file's own "muddy orange/brown" cell
 * comment) — never the earlier 0.55 alpha that read muddy over green.
 *
 * Also this wave (EPIC-94, item 3/AC5 — client: "Compact 'Mapa de control
 * por equipo' so everything reads together... zone percentages and lanes go
 * on one line under the pitch instead of stacked lists"): `ControlCard`'s
 * crest+name row moves from above the pitch to below it (never above,
 * satisfies "beside or below"); the three zone percentages move from a
 * vertically-stacked `space-y-1` column to one wrapping line; the lanes row
 * loses its own bordered/titled block (added by PMA-COCK-169 — a dated,
 * explicit reversal of that UC's "separated lanes block" decision) and
 * becomes a second plain one-line row directly under the zones line, same
 * treatment. The `sm:grid-cols-2` side-by-side breakpoint above is
 * untouched.
 */
import React, { useMemo } from "react";
import TeamLogo from "../../../../components/TeamLogo";
import {
  computeTeamControlMap,
  type TeamControlMapData,
  type TeamSide,
} from "../../utils/teamControlMap";
import {
  ZONES,
  PITCH_WIDTH,
  PITCH_HEIGHT,
  ZONE_COLS,
  ZONE_W,
} from "../../utils/heatMapZones";
import {
  PENALTY_ARC_Y_OFFSET,
  PITCH_MARKINGS,
  PITCH_MARKINGS_DERIVED,
} from "../../utils/pitchGeometry";
import { resolveMatchTeamColors } from "./resolveMatchTeamColors";
import { CrestDirectionCaption, ROW_PITCH_ASPECT } from "./pitchRowLayout";
import AttackDirectionCaption from "../atoms/AttackDirectionCaption";
import PitchArrowsOverlay from "../atoms/PitchArrowsOverlay";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match, Team } from "../../types";

/* ------------------------------------------------------------------ */
/*  Pitch markup constants (all in StatsBomb coordinate units)         */
/* ------------------------------------------------------------------ */

const PA_WIDTH = PITCH_MARKINGS.penaltyAreaDepth;
const PA_HEIGHT = PITCH_MARKINGS.penaltyAreaHeight;
const PA_Y = PITCH_MARKINGS_DERIVED.penaltyAreaY;

const GA_WIDTH = PITCH_MARKINGS.goalAreaDepth;
const GA_HEIGHT = PITCH_MARKINGS.goalAreaHeight;
const GA_Y = PITCH_MARKINGS_DERIVED.goalAreaY;

const CC_RX = PITCH_MARKINGS.centerCircleRx;
const CC_RY = PITCH_MARKINGS.centerCircleRy;
const PEN_X = PITCH_MARKINGS.penaltyMarkX;
const PEN_R = PITCH_MARKINGS.penaltyMarkRadius;
const CORNER_R = PITCH_MARKINGS.cornerArcR;

/* ------------------------------------------------------------------ */
/*  Color helpers                                                      */
/* ------------------------------------------------------------------ */

/** Cells with activity always show, scaling from 0.25 (min visible) to 0.85
 * (matches AreaControlGrid / Momentum / Recovery's full-strength alpha). */
const MIN_VISIBLE_ALPHA = 0.25;
const MAX_ALPHA = 0.85;

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!m) return `rgba(100, 116, 139, ${alpha})`;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function cellFill(color: string, intensity: number): string {
  if (intensity <= 0) return "rgba(0,0,0,0)";
  const alpha = MIN_VISIBLE_ALPHA + intensity * (MAX_ALPHA - MIN_VISIBLE_ALPHA);
  return hexToRgba(color, alpha);
}

/**
 * Legibility guard (lead review, EPIC-94 item 6/AC8 follow-up, 2026-09-23):
 * "Real teams wear green" — the reversal to the green pitch (`#2d6a2e`)
 * makes a green team color's translucent cell tint read as invisible
 * against the background. Reproducible with the app's own hash-based
 * default palette, not a fixture quirk: `PALETTE_DEFS[1].primary` in
 * `teamPalette.ts` is `#15803d` (green-700), one of 8 slots a team with no
 * custom color hashes into.
 *
 * A plain WCAG luminance-contrast check against `#2d6a2e` does NOT work
 * here: every dark, saturated color (navy blue, dark red) computes a
 * similarly low ratio against a dark background regardless of hue, so it
 * would false-positive on perfectly legible team colors (measured: blue
 * `#2563eb` 1.26:1, red `#dc2626` 1.35:1 — both clearly distinguishable
 * from a green pitch by eye, both would fail a luminance-only ~2:1 floor).
 * The real problem is HUE, not luminance, so this compares hue distance
 * instead: colors within `GREEN_HUE_DISTANCE_THRESHOLD` degrees of the
 * pitch's own hue (measured 121°) are flagged, colors further away are
 * not, regardless of how dark either one is. `GREEN_SATURATION_FLOOR`
 * excludes near-grey/near-white/near-black colors, whose numeric hue is
 * unstable at low saturation and not perceptually "a color" at all.
 * Calibrated against sampled hex values (see the wave's own
 * `team_findings.md` for the full table): real greens (kit greens,
 * `#15803d`, `#16a34a`, `#22c55e`) land at 2-21° and are flagged; distinct
 * hues already used in this app (`#10b981` emerald accent, blue, red,
 * amber) land at 39°+ and are not.
 */
function hexToHueSat(hex: string): { h: number; s: number } | null {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0);
      break;
    case g:
      h = (b - r) / d + 2;
      break;
    default:
      h = (r - g) / d + 4;
  }
  return { h: h * 60, s: s * 100 };
}

const PITCH_GREEN_HEX = "#2d6a2e";
const PITCH_GREEN_HUE = hexToHueSat(PITCH_GREEN_HEX)?.h ?? 121;
const GREEN_HUE_DISTANCE_THRESHOLD = 30; // degrees
const GREEN_SATURATION_FLOOR = 15; // percent

/** True when `color` is close enough in hue to the pitch green that its
 * translucent cell fill would read as invisible against the pitch. */
function isNearPitchGreen(color: string): boolean {
  const hs = hexToHueSat(color);
  if (!hs || hs.s < GREEN_SATURATION_FLOOR) return false;
  const diff = Math.abs(hs.h - PITCH_GREEN_HUE) % 360;
  const dist = diff > 180 ? 360 - diff : diff;
  return dist <= GREEN_HUE_DISTANCE_THRESHOLD;
}

/* ------------------------------------------------------------------ */
/*  Per-team card                                                      */
/* ------------------------------------------------------------------ */

interface ControlCardProps {
  team: Team;
  side: TeamSide;
  data: TeamControlMapData;
  color: string;
  t: (key: string, fallback?: string) => string;
  /** EPIC-97 AC6 (client wave 2026-09-25) — see `TeamControlMap`'s own prop
   * doc below. Default false (unchanged rendering). */
  rotated?: boolean;
  /** EPIC-110 D2: draw the `-pitch-arrows` overlay on the pitch. */
  pitchArrows?: boolean;
  /** EPIC-110 D2: tighter padding so three pitches fit one row at lg. */
  compact?: boolean;
  /** EPIC-111 D4: team-row column (no own border/padding, shared pitch box,
   * crest + direction caption, no zones/lanes lines). */
  rowLayout?: boolean;
  "data-testid"?: string;
}

export const ControlCard: React.FC<ControlCardProps> = ({
  team,
  side,
  data,
  color,
  t,
  rotated = false,
  pitchArrows = false,
  compact = false,
  rowLayout = false,
  "data-testid": testId,
}) => {
  const teamLabel = team.short_name || team.name;
  const isEmpty = data.total === 0;
  const cellByZoneId = new Map(data.cells.map((c) => [c.zoneId, c]));
  // Lead review, EPIC-94 item 6/AC8 follow-up: a green team color needs a
  // legibility outline against the green pitch — see isNearPitchGreen's
  // own doc comment above.
  const lowLegibility = isNearPitchGreen(color);

  // EPIC-94, item 3/AC5: crest + name moves below the pitch (never above).
  // Own testid (not just team-name text) so its DOM position can be
  // asserted unambiguously — the team's short name also appears in the
  // pitch svg's own aria-label/<title>.
  //
  // Lead review follow-up (2026-09-23): the crest was still `size="xl"`
  // (64px, TeamLogo.tsx's own token table) — a large placeholder box that
  // spent the vertical space the compaction was meant to save. Down to
  // `size="xs"` (24px) and `text-xs` name, matching the ~24-28px the lead
  // asked for.
  const crestOnly = (
    <div
      className="flex items-center gap-1.5"
      data-testid={testId ? `${testId}-crest` : undefined}
    >
      <TeamLogo team={team} size="xs" bare />
      <span className="text-xs font-semibold text-slate-200">{teamLabel}</span>
    </div>
  );

  // Lead review follow-up: crest + name + attacking-direction now share ONE
  // row under the pitch (was crest alone, then a separate
  // `AttackDirectionCaption` row below it) — only used in the non-empty
  // branch, where a direction is meaningful. The empty branch keeps
  // `crestOnly` alone, unchanged. Same `-crest` testid as `crestOnly` so
  // no caller/test needs to know which variant rendered.
  const crestAndDirectionRow = (
    <div
      className="flex w-full max-w-[320px] flex-wrap items-center justify-between gap-x-3 gap-y-1"
      data-testid={testId ? `${testId}-crest` : undefined}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <TeamLogo team={team} size="xs" bare />
        <span className="truncate text-xs font-semibold text-slate-200">
          {teamLabel}
        </span>
      </div>
      <AttackDirectionCaption
        side={side}
        t={t}
        reversed={rotated}
        data-testid={testId}
      />
    </div>
  );

  // EPIC-111 D4: crest + "Dirección de ataque" caption (team code dropped).
  const rowCaption = (
    <CrestDirectionCaption
      team={team}
      side={side}
      t={t}
      rotated={rotated}
      captionTestId={testId}
      crestTestId={testId ? `${testId}-crest` : undefined}
    />
  );

  return (
    <div
      className={
        rowLayout
          ? "flex flex-col items-center gap-2"
          : `flex flex-col items-center gap-3 rounded-lg border border-slate-700 bg-slate-900/40 ${
              compact ? "p-2" : "p-4"
            }`
      }
      data-testid={testId}
    >
      {isEmpty ? (
        <>
          <div
            className={`flex w-full items-center justify-center rounded-lg border border-dashed border-slate-700 px-4 text-center text-xs text-slate-400 ${
              rowLayout ? "" : "h-40 max-w-[320px]"
            }`}
            style={rowLayout ? { aspectRatio: ROW_PITCH_ASPECT } : undefined}
            data-testid={testId ? `${testId}-empty` : undefined}
          >
            {t(
              "analytics.controlMapEmpty",
              "Not enough location data yet to show the control map",
            )}
          </div>
          {rowLayout ? rowCaption : crestOnly}
        </>
      ) : (
        <>
          <div
            className={`relative w-full ${rowLayout ? "" : "max-w-[320px]"} rounded-lg border border-slate-700 bg-green-800 overflow-hidden`}
            style={{
              aspectRatio: `${PITCH_WIDTH + 4} / ${PITCH_HEIGHT + 4}`,
              transform: rotated ? "rotate(180deg)" : undefined,
            }}
            data-rotated={rotated ? "true" : undefined}
          >
            <svg
              viewBox={`-2 -2 ${PITCH_WIDTH + 4} ${PITCH_HEIGHT + 4}`}
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={teamLabel}
              data-testid={testId ? `${testId}-svg` : undefined}
            >
              <title>{teamLabel}</title>

              {/* ─── Field background ─── */}
              {/* EPIC-94, item 6/AC8: dated reversal of PMA-COCK-212's light
                  grey — back to the area-control green (`#2d6a2e`, the exact
                  fill `AreaControlGrid.tsx`/`PassMapPitch.tsx` use), so every
                  pitch in the app shares one colour again per the client's
                  explicit request. */}
              <rect
                x={0}
                y={0}
                width={PITCH_WIDTH}
                height={PITCH_HEIGHT}
                fill="#2d6a2e"
                rx={1}
              />

              {/* ─── Mowing stripes ─── */}
              {/* Back to the white-on-green tone `AreaControlGrid.tsx` uses,
                  reversing PMA-COCK-212's dark-on-light inversion. */}
              {Array.from({ length: ZONE_COLS }, (_, i) =>
                i % 2 === 0 ? (
                  <rect
                    key={`stripe-${i}`}
                    x={i * ZONE_W}
                    y={0}
                    width={ZONE_W}
                    height={PITCH_HEIGHT}
                    fill="rgba(255,255,255,0.03)"
                  />
                ) : null,
              )}

              {/* ─── Team control-intensity tint ─── */}
              {ZONES.map((zone) => {
                const cell = cellByZoneId.get(zone.id);
                const intensity = cell?.intensity ?? 0;
                // Only an active (tinted) cell needs the legibility
                // outline — an intensity-0 cell already renders fully
                // transparent, nothing to distinguish from the pitch.
                const showLegibilityOutline = lowLegibility && intensity > 0;
                return (
                  <rect
                    key={`cell-${zone.id}`}
                    x={zone.x0}
                    y={zone.y0}
                    width={zone.x1 - zone.x0}
                    height={zone.y1 - zone.y0}
                    fill={cellFill(color, intensity)}
                    stroke={
                      showLegibilityOutline
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(15, 23, 42, 0.35)"
                    }
                    strokeWidth={showLegibilityOutline ? 0.6 : 0.25}
                    data-testid={
                      testId ? `${testId}-zone-${zone.id}` : undefined
                    }
                    data-zone-id={zone.id}
                    data-zone-intensity={intensity}
                    data-low-contrast={
                      showLegibilityOutline ? "true" : undefined
                    }
                  />
                );
              })}

              {/* ─── Pitch markings ─── */}
              {/* EPIC-94, item 6/AC8: back to white on the reinstated green
                  background — reverses PMA-COCK-212's dark-slate markings,
                  matching `AreaControlGrid.tsx`/`PassMapPitch.tsx`'s own
                  `rgba(255,255,255,...)` tone. */}
              <g stroke="rgba(255,255,255,0.55)" strokeWidth={0.4} fill="none">
                <rect
                  x={0}
                  y={0}
                  width={PITCH_WIDTH}
                  height={PITCH_HEIGHT}
                  rx={0.5}
                />
                <line
                  x1={PITCH_WIDTH / 2}
                  y1={0}
                  x2={PITCH_WIDTH / 2}
                  y2={PITCH_HEIGHT}
                />
                <ellipse
                  cx={PITCH_WIDTH / 2}
                  cy={PITCH_HEIGHT / 2}
                  rx={CC_RX}
                  ry={CC_RY}
                />
                <circle
                  cx={PITCH_WIDTH / 2}
                  cy={PITCH_HEIGHT / 2}
                  r={PEN_R}
                  fill="rgba(255,255,255,0.55)"
                />
                <rect x={0} y={PA_Y} width={PA_WIDTH} height={PA_HEIGHT} />
                <rect
                  x={PITCH_WIDTH - PA_WIDTH}
                  y={PA_Y}
                  width={PA_WIDTH}
                  height={PA_HEIGHT}
                />
                <rect x={0} y={GA_Y} width={GA_WIDTH} height={GA_HEIGHT} />
                <rect
                  x={PITCH_WIDTH - GA_WIDTH}
                  y={GA_Y}
                  width={GA_WIDTH}
                  height={GA_HEIGHT}
                />
                <circle
                  cx={PEN_X}
                  cy={PITCH_HEIGHT / 2}
                  r={PEN_R}
                  fill="rgba(255,255,255,0.55)"
                />
                <circle
                  cx={PITCH_WIDTH - PEN_X}
                  cy={PITCH_HEIGHT / 2}
                  r={PEN_R}
                  fill="rgba(255,255,255,0.55)"
                />
                <path
                  d={`M ${PA_WIDTH} ${
                    PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
                  } A ${CC_RX} ${CC_RY} 0 0 1 ${PA_WIDTH} ${
                    PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
                  }`}
                />
                <path
                  d={`M ${PITCH_WIDTH - PA_WIDTH} ${
                    PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
                  } A ${CC_RX} ${CC_RY} 0 0 0 ${PITCH_WIDTH - PA_WIDTH} ${
                    PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
                  }`}
                />
                <path
                  d={`M 0 ${CORNER_R} A ${CORNER_R} ${CORNER_R} 0 0 1 ${CORNER_R} 0`}
                />
                <path
                  d={`M ${
                    PITCH_WIDTH - CORNER_R
                  } 0 A ${CORNER_R} ${CORNER_R} 0 0 1 ${PITCH_WIDTH} ${CORNER_R}`}
                />
                <path
                  d={`M 0 ${
                    PITCH_HEIGHT - CORNER_R
                  } A ${CORNER_R} ${CORNER_R} 0 0 0 ${CORNER_R} ${PITCH_HEIGHT}`}
                />
                <path
                  d={`M ${
                    PITCH_WIDTH - CORNER_R
                  } ${PITCH_HEIGHT} A ${CORNER_R} ${CORNER_R} 0 0 0 ${PITCH_WIDTH} ${
                    PITCH_HEIGHT - CORNER_R
                  }`}
                />
              </g>
            </svg>
            {pitchArrows && (
              <PitchArrowsOverlay
                side={side}
                reversed={rotated}
                data-testid={testId ? `${testId}-pitch-arrows` : undefined}
              />
            )}
          </div>

          {rowLayout ? rowCaption : crestAndDirectionRow}

          {!rowLayout && (
            <>
              {/* EPIC-94, item 3/AC5: zone percentages on one line.
              Lead review follow-up (2026-09-23): the first pass
              (`text-sm sm:text-base`, no separators) was LARGER than this
              card's own stats-table text and wrapped onto two lines at
              1024px. A first fix to `text-xs` (12px) STILL wrapped —
              measured (off-screen nowrap clone): natural content width
              340px against a 320px available width. `text-[11px]` (this
              app's own compact-label convention, e.g. `PlayerCard.tsx`'s
              stat labels) plus `gap-x-1` (was `gap-x-1.5`) brings the
              natural width under 320px with margin. Given short `|`
              separators like the lanes line below, and each item is
              `whitespace-nowrap` so a break can only happen BETWEEN items,
              never mid-phrase. Phone width may still wrap (not a
              requirement there).
              EPIC-107 D5 (2026-10-06): the client asked for bigger, whiter
              captions, so this row (and the lanes row) is now `text-xs`
              (was `text-[11px]`), which exceeds 320px natural width: the
              zones and lanes lines MAY WRAP to two lines at 320px (items
              stay `whitespace-nowrap`, so a break is only between items).
              The "no wrap at 320px" intent above is superseded for this
              size; the checked requirement is no horizontal page overflow
              at 320px. */}
              <div
                className="flex w-full max-w-[320px] flex-wrap items-center justify-center gap-x-1 gap-y-1 text-xs text-slate-100"
                data-testid={testId ? `${testId}-zones` : undefined}
              >
                <span
                  className="whitespace-nowrap"
                  data-testid={testId ? `${testId}-safe` : undefined}
                >
                  {`${data.thirds.safe}% ${t("analytics.controlMapSafeZone", "Safe zone")}`}
                </span>
                <span aria-hidden="true">|</span>
                <span
                  className="whitespace-nowrap"
                  data-testid={testId ? `${testId}-middle` : undefined}
                >
                  {`${data.thirds.middle}% ${t("analytics.controlMapMiddleZone", "Middle zone")}`}
                </span>
                <span aria-hidden="true">|</span>
                <span
                  className="whitespace-nowrap"
                  data-testid={testId ? `${testId}-danger` : undefined}
                >
                  {`${data.thirds.danger}% ${t("analytics.controlMapDangerZone", "Dangerous attack")}`}
                </span>
              </div>

              {/* EPIC-94, item 3/AC5: lanes on one line directly under the
              zones line, no separate bordered block or "Lanes" heading —
              a dated reversal of PMA-COCK-169's "separated lanes block with
              its own heading and divider" decision. Each value already
              names its own lane ("Left"/"Center"/"Right"), so the heading
              was redundant once the two rows read together. Lead review
              follow-up: `text-[11px]` (was `text-sm`) to match the zones
              line above now that both are the same compact size; each
              item `whitespace-nowrap` for the same reason as the zones
              line. */}
              <div
                className="flex w-full max-w-[320px] flex-wrap items-center justify-center gap-x-1 gap-y-1 text-xs text-slate-200"
                data-testid={testId ? `${testId}-lanes` : undefined}
              >
                <span className="whitespace-nowrap">{`${data.lanes.left}% ${t("analytics.controlMapLaneLeft", "Left")}`}</span>
                <span aria-hidden="true">|</span>
                <span className="whitespace-nowrap">{`${data.lanes.center}% ${t("analytics.controlMapLaneCenter", "Center")}`}</span>
                <span aria-hidden="true">|</span>
                <span className="whitespace-nowrap">{`${data.lanes.right}% ${t("analytics.controlMapLaneRight", "Right")}`}</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface TeamControlMapProps {
  match: Match;
  events: MatchEvent[];
  t: (key: string, fallback?: string) => string;
  /**
   * EPIC-97 AC6 (client wave 2026-09-25, "En el segundo tiempo a esto se le
   * puede dar vuelta"): true renders both team pitches rotated 180 degrees
   * (display-only — `computeTeamControlMap`'s own thirds/lanes percentages,
   * still computed from the same unrotated stored frame, are untouched) and
   * flips each card's attacking-direction arrow to match. Used by
   * `PeriodMapsSection` for periods 2 and 4 only; the whole-match mount
   * (`HeatMapSection`) omits it (default false, unchanged rendering).
   */
  rotated?: boolean;
  /**
   * EPIC-97 AC6: overrides the default "for the whole match" caption text —
   * `PeriodMapsSection` passes a period-scoped string (e.g. "...for the
   * First Half.") since this component is also mounted once per period
   * block, where the default text is simply wrong (the map covers one
   * period, not the whole match). Omitted by the whole-match mount, which
   * keeps the default `analytics.controlMapCaption` text.
   */
  captionOverride?: string;
  /**
   * EPIC-110 D2: replaces the default two-card grid with a caller-composed
   * layout (per-team rows). The title/caption and the root testid are kept.
   * Each card is a ready-made `ControlCard` (with pitch arrows).
   */
  renderCards?: (ctx: {
    cards: Record<TeamSide, React.ReactNode>;
    colors: Record<TeamSide, string>;
  }) => React.ReactNode;
  /** EPIC-110: mark the title/caption block `data-export-hidden` (the
   * whole-match PDF capture draws its own control-map section). */
  exportHiddenHeader?: boolean;
  /** EPIC-111 D4: i18n key for the section title (default `controlMapTitle`). */
  titleKey?: string;
  "data-testid"?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const TeamControlMap: React.FC<TeamControlMapProps> = ({
  match,
  events,
  t,
  rotated = false,
  captionOverride,
  renderCards,
  exportHiddenHeader = false,
  titleKey = "analytics.controlMapTitle",
  "data-testid": testId,
}) => {
  const homeTeamId = match.home_team.id;
  const awayTeamId = match.away_team.id;

  const homeData = useMemo(
    () => computeTeamControlMap(events, homeTeamId, "home"),
    [events, homeTeamId],
  );
  const awayData = useMemo(
    () => computeTeamControlMap(events, awayTeamId, "away"),
    [events, awayTeamId],
  );

  const { home: homeColor, away: awayColor } = useMemo(
    () => resolveMatchTeamColors(match),
    [match.home_team, match.away_team],
  );

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div data-export-hidden={exportHiddenHeader ? "true" : undefined}>
        <h3
          data-testid={testId ? `${testId}-title` : undefined}
          className="text-sm font-semibold text-slate-200 uppercase tracking-wider"
        >
          {t(
            titleKey,
            titleKey === "analytics.controlMapTitle"
              ? "Team Control Map"
              : "Heat and control map by team",
          )}
        </h3>
        <p
          data-testid={testId ? `${testId}-caption` : undefined}
          className="text-sm text-slate-200"
        >
          {captionOverride ??
            t(
              "analytics.controlMapCaption",
              "Share of each team's located events by pitch third and lane, oriented to its attacking direction for the whole match.",
            )}
        </p>
      </div>

      {/* Client wave 2026-09-19 (brief 6/AC11): "both teams and their zone
          and lane figures readable together" — the old `md:` (768px)
          breakpoint left this stacked (home card, then away card below the
          fold) on anything narrower than a tablet, which is most of this
          PUBLIC page's phone-width traffic. Lowered to `sm:` (640px, the
          narrowest standard Tailwind breakpoint short of "always on") and
          each card's own max-width trimmed 384px -> 320px (`max-w-[320px]`
          throughout this file) so two cards plausibly fit side by side at
          that width without the pitch diagram becoming illegibly small. */}
      {renderCards ? (
        renderCards({
          cards: {
            home: (
              <ControlCard
                team={match.home_team}
                side="home"
                data={homeData}
                color={homeColor}
                t={t}
                rotated={rotated}
                pitchArrows
                compact
                rowLayout
                data-testid={testId ? `${testId}-home` : undefined}
              />
            ),
            away: (
              <ControlCard
                team={match.away_team}
                side="away"
                data={awayData}
                color={awayColor}
                t={t}
                rotated={rotated}
                pitchArrows
                compact
                rowLayout
                data-testid={testId ? `${testId}-away` : undefined}
              />
            ),
          },
          colors: { home: homeColor, away: awayColor },
        })
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ControlCard
            team={match.home_team}
            side="home"
            data={homeData}
            color={homeColor}
            t={t}
            rotated={rotated}
            data-testid={testId ? `${testId}-home` : undefined}
          />
          <ControlCard
            team={match.away_team}
            side="away"
            data={awayData}
            color={awayColor}
            t={t}
            rotated={rotated}
            data-testid={testId ? `${testId}-away` : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default TeamControlMap;
